import { BannerUploadEntry, buildPersistedBannerImages } from "./bannerThemeUtils";

export type StorefrontSection = "identity" | "catalogue" | "banners" | "delivery" | "social" | "access" | "payment";
export interface DeliveryZoneValue { id?: number; name: string; delivery_fee: number; is_active: boolean }

export const parseStoreTheme = (store: any): Record<string, any> => {
  const value = store?.theme_config;
  if (!value) return {};
  return typeof value === "string" ? JSON.parse(value) : value;
};

export const hexColor = (value: any, fallback = "#173f5f"): string => {
  const color = typeof value?.toHexString === "function" ? value.toHexString() : value;
  return typeof color === "string" && /^#?[\da-f]{6}$/i.test(color)
    ? `#${color.replace(/^#/, "")}` : fallback;
};

export const storefrontFormValues = (store: any) => {
  const theme = parseStoreTheme(store);
  return {
    primary_color: hexColor(theme.primary_color),
    secondary_color: hexColor(theme.secondary_color, "#e63b3b"),
    header_background_color: hexColor(theme.header_background_color, hexColor(theme.primary_color)),
    header_elements_color: hexColor(theme.header_elements_color, "#ffffff"),
    font_family: theme.font_family || "Inter", footer_text: theme.footer_text || "",
    show_search: theme.show_search !== false, show_categories: theme.show_categories !== false,
    products_per_page: theme.products_per_page ?? 12,
    facebook: theme.social_links?.facebook || "", instagram: theme.social_links?.instagram || "",
    twitter: theme.social_links?.twitter || "", whatsapp: theme.social_links?.whatsapp || "",
    autoplay: theme.carousel_settings?.autoplay !== false,
    autoplay_delay: theme.carousel_settings?.autoplay_delay ?? 5000,
    show_dots: theme.carousel_settings?.show_dots !== false, show_arrows: theme.carousel_settings?.show_arrows !== false,
    transition_effect: theme.carousel_settings?.transition_effect || "slide",
    delivery_policy: store?.delivery_policy || "", return_policy: store?.return_policy || "",
    delivery_timeframe: store?.delivery_timeframe || "", min_order_amount: Number(store?.min_order_amount ?? 0),
    delivery_cost_info: store?.delivery_cost_info || "",
    delivery_zones: (Array.isArray(store?.delivery_zones) ? store.delivery_zones : []).map((zone: any): DeliveryZoneValue => ({
      ...(Number(zone.id) > 0 ? { id: Number(zone.id) } : {}), name: String(zone.name || ""),
      delivery_fee: Number(zone.delivery_fee || 0), is_active: zone.is_active !== false && Number(zone.is_active) !== 0,
    })),
  };
};

export const storefrontBanners = (store: any) => {
  const theme = parseStoreTheme(store);
  const banners = Array.isArray(theme.banner_images) && theme.banner_images.length
    ? [...theme.banner_images].sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
    : theme.banner_image ? [{ url: theme.banner_image }] : [];
  const desktop: BannerUploadEntry[] = banners.map((banner, order) => ({
    ...banner, uid: `banner-${order}`, name: `Bannière ${order + 1}`, status: "done", order,
  }));
  const mobile: BannerUploadEntry[] = banners.flatMap((banner, order) => banner.mobile_custom_url ? [{
    uid: `mobile-${order}`, name: `Version mobile ${order + 1}`, status: "done", url: banner.mobile_custom_url, order,
  }] : []);
  return { desktop, mobile };
};

// Comparer seulement les données éditées, pas les métriques ou métadonnées de lecture.
export const storefrontFingerprint = (store: any) => {
  const { desktop, mobile } = storefrontBanners(store);
  return JSON.stringify({ values: storefrontFormValues(store), banners: buildPersistedBannerImages(desktop, mobile) });
};

export const buildStorefrontTheme = (
  values: any, desktop: BannerUploadEntry[], mobile: BannerUploadEntry[], store: any
) => {
  const banners = buildPersistedBannerImages(desktop, mobile);
  return {
    ...parseStoreTheme(store),
    primary_color: hexColor(values.primary_color), secondary_color: hexColor(values.secondary_color, "#e63b3b"),
    header_background_color: hexColor(values.header_background_color, hexColor(values.primary_color)),
    header_elements_color: hexColor(values.header_elements_color, "#ffffff"), font_family: values.font_family,
    footer_text: values.footer_text, banner_image: banners[0]?.url || "", banner_images: banners,
    show_search: values.show_search, show_categories: values.show_categories, products_per_page: values.products_per_page,
    carousel_settings: { autoplay: values.autoplay, autoplay_delay: values.autoplay_delay, show_dots: values.show_dots,
      show_arrows: values.show_arrows, transition_effect: values.transition_effect },
    social_links: { facebook: values.facebook, instagram: values.instagram, twitter: values.twitter, whatsapp: values.whatsapp },
  };
};

export const storefrontFieldSection = (name: string): StorefrontSection => {
  if (["facebook", "instagram", "twitter", "whatsapp"].includes(name)) return "social";
  if (name.startsWith("delivery_") || ["return_policy", "min_order_amount"].includes(name)) return "delivery";
  if (["show_search", "show_categories", "products_per_page", "autoplay", "autoplay_delay", "show_dots", "show_arrows", "transition_effect"].includes(name)) return "catalogue";
  return "identity";
};
